import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import Swal from 'sweetalert2';
import { CampaignService } from '../../services/campaigns/campaign.service';
import { VapiService } from '../../services/vapi/vapi.service';
import { AiAgentsService } from '../../services/ai-agents/ai-agents.service';
import { AvailablePhoneNumbersService } from '../../services/available-phone-numbers/available-phone-numbers.service';
import { LeadService } from '../../services/lead/lead.service';
import { getFirebaseBackend } from 'src/app/authUtils';
import { AvailablePhoneNumbersComponent } from './available-phone-numbers/available-phone-numbers.component';
import { AI_AGENT_CATALOG } from 'src/app/shared/constants';
import {
	AiAgent,
	Assignment,
	AutonomyMode,
	AUTONOMY_MODES,
	AI_AGENTS,
	DIALER_AGENTS,
	INBOUND_AGENTS,
	TEXT_AGENTS,
	VOICE_AGENTS,
	WorkQueue,
	WORK_QUEUES,
} from './ai-agents.roster';

type TabKey = 'floor' | 'agents' | 'stats';
type StatsPeriod = 'daily' | 'weekly' | 'monthly';
type WaterfallRow = { campaign_id: number; remaining: boolean };

/** Per-agent working window + cadence (local state; prefilled from the RingCentral window). Times are Pacific — the LMS call window is documented in PT. */
interface Schedule {
	windowStart: string; // "HH:MM"
	windowEnd: string;
	activeDays: string[];
	dailyTarget: number;
	weeklyTarget: number;
}

const PT_TIMEZONE = 'America/Los_Angeles';
/** localStorage key of the Live floor's "Hide finished" switch, so the choice survives a reload. */
const HIDE_FINISHED_KEY = 'aiAgents.hideFinished';

/**
 * AI Agents — orchestration control surface for the real LMS agent roster, backed by the Vapi
 * integration (models/AiAgents.php + models/Vapi.php on the API). Everything for a voice dialer
 * (pause/autonomy/campaign queue/schedule) is managed from this one tab — no more Work
 * Queues / Scheduler tabs, no more Manual Launch panel. Each dialer works its own ordered
 * campaign waterfall: AiAgents::dispatchQueue() (no campaignId given) resolves the first
 * assigned campaign that still has eligible targets and dials only that one — the backend
 * decides which campaign is "active", this UI only edits the order.
 *
 * Pause for a voice dialer is QUEUE-level (ai_queue_assignment.paused) — that's what
 * AiAgents::checkDispatchGate() enforces. There's no agent-level pause anymore (it had no UI
 * control and could silently block dispatch with no way to clear it — removed on both ends).
 * autonomyByAgent stays in use for Margot (inbound) and the text agents, which have no queue.
 */
@Component({
	selector: 'app-ai-agents',
	templateUrl: './ai-agents.component.html',
	styleUrls: ['./ai-agents.component.scss'],
})
export class AiAgentsComponent implements OnInit, OnDestroy {
	/** Backed by the ai_agents_descriptions table (vapi/get_agents) — falls back to the static roster until it loads. */
	voiceAgents: AiAgent[] = VOICE_AGENTS;
	readonly inboundAgents = INBOUND_AGENTS;
	readonly textAgents = TEXT_AGENTS;
	readonly dialerAgents = DIALER_AGENTS;
	/** Only Ivy/Parker/Renee are exposed in the roster UI for now — everything else stays wired but hidden. */
	readonly visibleAgentKeys = ['ivy', 'parker', 'renee'];
	/** Inbound/text agents have no members in visibleAgentKeys — their whole sections stay hidden until that changes. */
	readonly showInboundAgents = false;
	readonly showTextAgents = false;
	readonly agents = AI_AGENTS;
	readonly queues = WORK_QUEUES;
	readonly autonomyModes = AUTONOMY_MODES;
	readonly scheduleDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

	// ── Tabs (default Agents — Live floor is pending-empty) ──
	readonly tabs: { key: TabKey; label: string }[] = [
		{ key: 'floor', label: 'Live floor' },
		{ key: 'agents', label: 'Agents' },
		{ key: 'stats', label: 'Stats' },
	];
	tab: TabKey = 'agents';

	// ── Live guardrail config (static references — real values, not editable telemetry) ──
	readonly callWindowLabel = '6:00 AM – 2:30 PM PT · Mon–Fri';
	readonly transferDestination = 'Toplead Sales · (650) 399-3777 ext. 4 · warm + summary · Glen + Sara';
	recordingConsent = true;
	readonly costCap = 50;

	// ── Local orchestration state ──
	enabled = new Set<string>(AI_AGENTS.map((a) => a.key));
	autonomyByAgent: Record<string, AutonomyMode> = {}; // Margot + text agents only (voice dialers use autonomyByQueue)
	autonomyByQueue: Record<string, AutonomyMode> = {};
	assignments: Record<string, Assignment> = {};
	pausedQueues = new Set<string>();

	// ── Schedule (per dialer agent), prefilled to the live RingCentral window — Pacific time ──
	schedules: Record<string, Schedule> = {};

	// ── Waterfall status per queue (campaign_id → remaining?), powers the active/exhausted tags ──
	waterfallStatus: Record<string, WaterfallRow[]> = {};

	// ── Stats period ──
	period: StatsPeriod = 'daily';
	readonly periods: StatsPeriod[] = ['daily', 'weekly', 'monthly'];

	// ── Campaigns (for the waterfall picker) ──
	campaigns: any[] = [];

	// ── Configure modal (pause/campaign waterfall/schedule for one dialer) ──
	configQueue: WorkQueue | null = null;
	configAgent: AiAgent | null = null;
	formCampaignIds: number[] = [];
	campaignSearch = '';
	campaignPickerOpen = false;
	campaignAddOpen = false;
	scheduleFormOpen = false;
	/** Vapi phone number picked in the Configure modal — the instance about to be started dials from it. */
	selectedPhoneNumberId: string | null = null;
	@ViewChild('campaignSearchInput') campaignSearchInput?: ElementRef<HTMLInputElement>;
	/** The phone picker of whichever Configure modal is open. */
	@ViewChild(AvailablePhoneNumbersComponent) phonePicker?: AvailablePhoneNumbersComponent;

	// ── Parker's exclusive Configure modal — one campaign or all of them + a start date, then Start like Ivy (agents/start_instance) ──
	parkerConfigAgent: AiAgent | null = null;
	parkerMode: 'campaigns' | 'all_campaigns' | null = null;
	parkerStartDate = '';

	// ── Renee's exclusive Configure modal — one campaign or all of them (no date), then Start like Ivy (agents/start_instance) ──
	reneeConfigAgent: AiAgent | null = null;
	reneeMode: 'campaigns' | 'all_campaigns' | null = null;

	// ── Prospects modal — one instance's queued prospects (ai_agents_queue joined to prospects) ──
	prospectsAgent: AiAgent | null = null;
	prospectsTitle = '';
	prospectsLoading = false;
	prospects: any[] = [];

	// ── Calls modal — call log (ai_calls) of one instance, or of every instance of a catalog agent ──
	callsAgent: AiAgent | null = null;
	callsTitle = '';
	callsLoading = false;
	calls: any[] = [];

	// ── Call detail modal — recording (native <audio> player) on top, transcript chat below ──
	callDetail: any | null = null;
	recordingLoading = false;
	recordingUrl: string | null = null;
	transcriptMessages: { role: string; message: string; time?: number | null }[] = [];

	toast = '';
	private toastTimer: any = null;

	// ── Live floor — agent instances (ai_agents: active/paused, plus the finished ones) / Stats (real, from Vapi's own analytics) ──
	instances: any[] = [];
	instancesLoaded = false;
	/** Live floor switch: hides the finished instances, which otherwise stay listed so their calls and results can be reviewed. */
	hideFinished = false;
	/** Id of the instance a pause/resume/stop request is in flight for — disables its buttons meanwhile. */
	instanceBusyId: number | null = null;
	/** Vapi phone_number_id -> E.164 number, to show a readable number on the Live floor (ai_agents only stores the id). */
	private phoneNumberById: Record<string, string> = {};
	statsByAgent: Record<string, any> = {};
	private readonly userId: number | null;
	private readonly userLogin: string | null;

	constructor(private campaignSrv: CampaignService, private vapiSrv: VapiService, private aiAgentsSrv: AiAgentsService, private phoneNumbersSrv: AvailablePhoneNumbersService, private leadSrv: LeadService) {
		for (const a of AI_AGENTS) this.autonomyByAgent[a.key] = 'shadow';
		for (const q of WORK_QUEUES) {
			this.autonomyByQueue[q.id] = 'paused';
			this.assignments[q.id] = { agentKey: q.defaultAgentKey, campaignIds: [] };
		}
		// Prefill every dialer's window to the live RingCentral window: 6:00 AM–2:30 PM PT, Mon–Fri.
		for (const a of DIALER_AGENTS) {
			this.schedules[a.key] = {
				windowStart: '06:00',
				windowEnd: '14:30',
				activeDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
				dailyTarget: 60,
				weeklyTarget: 300,
			};
		}
		const authUser = getFirebaseBackend()?.getAuthenticatedUser();
		this.userId = authUser?.IdUser ?? null;
		this.userLogin = authUser?.Login ?? null;
		try {
			this.hideFinished = localStorage.getItem(HIDE_FINISHED_KEY) === '1';
		} catch {}
		this.campaignSrv.getCampaignsForSelect().subscribe((res: any) => {
			this.campaigns = Array.isArray(res?.body) ? res.body : [];
		});
	}

	private pollHandle: any = null;

	ngOnInit(): void {
		this.getAgents();
		this.refreshInstances();

		// No websocket/push in this stack — light polling is how the running instances' progress
		// (who each one is dialing right now, queue progress, an instance finishing or getting
		// paused by a failed call) shows up without a reload.
		this.pollHandle = setInterval(() => {
			if (this.tab === 'floor' || this.runningInstances.length) {
				this.refreshInstances();
			}
		}, 20000);
	}

	/**
	 * Pulls the AI agents catalog (ai_agents_descriptions table) and merges it onto VOICE_AGENTS: name,
	 * vapiId and description come from the backend row; key/role/kind/launch/queueId/icon stay
	 * from the static roster (the table has no columns for those) — matched via AI_AGENT_CATALOG's
	 * id -> key mapping. Falls back to the static roster untouched if the call fails or a row
	 * doesn't match a known agent.
	 */
	getAgents(): void {
		this.vapiSrv.getAgents().subscribe({
			next: (res: any) => {
				const rows: any[] = res?.body ?? res ?? [];
				const merged = rows
					.map((row) => {
						const key = AI_AGENT_CATALOG.find((c) => c.id === Number(row.id))?.key;
						const base = key ? VOICE_AGENTS.find((a) => a.key === key) : undefined;
						if (!base) return null;
						return {
							...base,
							name: row.name ?? base.name,
							vapiId: row.vapi_id ?? base.vapiId,
							description: row.description ?? base.description,
						};
					})
					.filter((a): a is AiAgent => !!a);
				if (merged.length) this.voiceAgents = merged;
			},
			error: () => this.showToast('Could not load AI agents — using local roster'),
		});
	}


	ngOnDestroy(): void {
		if (this.pollHandle) clearInterval(this.pollHandle);
	}

	setTab(t: TabKey): void {
		this.tab = t;
		if (t === 'floor') this.refreshInstances();
		if (t === 'stats') this.refreshStats();
	}

	// ── Lookups ──
	agentByKey(key: string | null): AiAgent | undefined {
		return key ? this.agents.find((a) => a.key === key) : undefined;
	}
	/** Voice dialer cards shown in the roster — filtered down to visibleAgentKeys. */
	get visibleVoiceAgents(): AiAgent[] {
		return this.voiceAgents.filter((a) => this.visibleAgentKeys.includes(a.key));
	}
	/** Stats tab KPI cards — same visibility filter as the roster. */
	get visibleDialerAgents(): AiAgent[] {
		return this.dialerAgents.filter((a) => this.visibleAgentKeys.includes(a.key));
	}
	assignmentFor(queueId: string): Assignment {
		return this.assignments[queueId] ?? { agentKey: null, campaignIds: [] };
	}
	campaignName(id: number): string {
		return this.campaigns.find((c) => c.id === id)?.name ?? `#${id}`;
	}

	// ── Enable toggle ──
	isEnabled(key: string): boolean {
		return this.enabled.has(key);
	}
	toggleEnabled(key: string): void {
		if (this.enabled.has(key)) this.enabled.delete(key);
		else this.enabled.add(key);
		this.persistAgentConfig(key);
	}

	// ── Autonomy — Margot/text agents (agent-level) ──
	setAgentAutonomy(agentKey: string, mode: string): void {
		this.autonomyByAgent[agentKey] = mode as AutonomyMode;
		this.persistAgentConfig(agentKey);
	}

	// ── Pause — voice dialers (queue-level, this is what the dispatch gate reads) ──
	isQueuePaused(id: string): boolean {
		return this.pausedQueues.has(id);
	}
	toggleQueuePause(id: string): void {
		if (this.pausedQueues.has(id)) this.pausedQueues.delete(id);
		else this.pausedQueues.add(id);
		this.persistQueueAssignment(id);
	}

	// ── Guardrail control ──
	toggleRecordingConsent(): void {
		this.recordingConsent = !this.recordingConsent;
		this.persistGlobalConfig();
	}

	// ── Schedule ──
	schedule(agentKey: string): Schedule {
		return this.schedules[agentKey];
	}
	setScheduleField(agentKey: string, field: 'windowStart' | 'windowEnd' | 'dailyTarget' | 'weeklyTarget', value: string): void {
		const s = this.schedules[agentKey];
		if (!s) return;
		if (field === 'windowStart' || field === 'windowEnd') s[field] = value;
		else s[field] = Math.max(0, parseInt(value, 10) || 0);
		this.persistSchedule(agentKey);
	}
	isDayActive(agentKey: string, day: string): boolean {
		return this.schedules[agentKey]?.activeDays.includes(day) ?? false;
	}
	toggleScheduleDay(agentKey: string, day: string): void {
		const s = this.schedules[agentKey];
		if (!s) return;
		const i = s.activeDays.indexOf(day);
		if (i >= 0) s.activeDays.splice(i, 1);
		else s.activeDays.push(day);
		this.persistSchedule(agentKey);
	}

	/** True when "now" (Pacific time — the LMS call window is documented in PT) falls outside the agent's configured window/active days. Informational only: the backend gate already blocks dispatch outside the window regardless of this UI hint. */
	isOutsideSchedule(agentKey: string): boolean {
		const s = this.schedules[agentKey];
		if (!s) return false;
		const { hm, day } = this.nowInPacific();
		if (!s.activeDays.includes(day)) return true;
		return hm < s.windowStart || hm > s.windowEnd;
	}
	private nowInPacific(): { hm: string; day: string } {
		const parts = new Intl.DateTimeFormat('en-US', {
			timeZone: PT_TIMEZONE,
			hour12: false,
			hour: '2-digit',
			minute: '2-digit',
			weekday: 'short',
		}).formatToParts(new Date());
		const hour = parts.find((p) => p.type === 'hour')?.value ?? '00';
		const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
		const day = parts.find((p) => p.type === 'weekday')?.value ?? '';
		return { hm: `${hour}:${minute}`, day };
	}

	// ── Persistence helpers — mirror local state to the backend after every mutation above ──
	private persistAgentConfig(agentKey: string): void {
		this.vapiSrv
			.saveAgentConfig(
				agentKey,
				{
					enabled: this.isEnabled(agentKey),
					autonomy_mode: this.autonomyByAgent[agentKey],
				},
				this.userId ?? undefined,
			)
			.subscribe({ error: () => this.showToast('Could not save agent config') });
	}
	private persistQueueAssignment(queueId: string): void {
		const a = this.assignmentFor(queueId);
		const agentId = AI_AGENT_CATALOG.find((x) => x.key === a.agentKey)?.id;
		this.vapiSrv
			.saveQueueAssignment(queueId, a.agentKey, agentId, a.campaignIds, this.autonomyByQueue[queueId], this.isQueuePaused(queueId), this.userId ?? undefined)
			.subscribe({ error: () => this.showToast('Could not save queue assignment') });
	}
	private persistSchedule(agentKey: string): void {
		const s = this.schedules[agentKey];
		if (!s) return;
		this.vapiSrv
			.saveSchedule(agentKey, {
				window_start: s.windowStart,
				window_end: s.windowEnd,
				active_days: s.activeDays,
				daily_target: s.dailyTarget,
				weekly_target: s.weeklyTarget,
			})
			.subscribe({ error: () => this.showToast('Could not save schedule') });
	}
	private persistGlobalConfig(): void {
		this.vapiSrv
			.saveGlobalConfig({ recording_consent: this.recordingConsent })
			.subscribe({ error: () => this.showToast('Could not save global config') });
	}

	/** null = status not loaded yet for this campaign. */
	campaignRemaining(queueId: string, campaignId: number): boolean | null {
		const row = (this.waterfallStatus[queueId] ?? []).find((r) => Number(r.campaign_id) === Number(campaignId));
		return row ? !!row.remaining : null;
	}
	/** The waterfall's "current" campaign: first one in orderedIds that still has remaining targets. */
	isCampaignActive(queueId: string, campaignId: number, orderedIds: number[]): boolean {
		if (this.campaignRemaining(queueId, campaignId) !== true) return false;
		for (const id of orderedIds) {
			if (id === campaignId) return true;
			if (this.campaignRemaining(queueId, id) === true) return false;
		}
		return false;
	}

	// ── Configure modal (pause/autonomy/campaign waterfall/schedule, one dialer at a time) ──
	openConfig(agent: AiAgent): void {
		if (agent.key === 'parker') {
			this.openParkerConfig(agent);
			return;
		}
		if (agent.key === 'renee') {
			this.openReneeConfig(agent);
			return;
		}
		if (!agent.queueId) return;
		const q = this.queues.find((x) => x.id === agent.queueId);
		if (!q) return;
		// Every Start creates a brand-new instance, so the campaign queue always opens empty.
		this.formCampaignIds = [];
		this.campaignSearch = '';
		this.campaignPickerOpen = false;
		this.campaignAddOpen = false;
		this.scheduleFormOpen = false;
		this.selectedPhoneNumberId = null;
		this.configQueue = q;
		this.configAgent = agent;
	}
	closeConfig(): void {
		this.configQueue = null;
		this.configAgent = null;
	}

	/** Campaigns matching the search box, already-added ones excluded — capped so the dropdown stays short. */
	filteredCampaigns(): any[] {
		const term = this.campaignSearch.trim().toLowerCase();
		return this.campaigns
			.filter((c) => !this.formCampaignIds.includes(c.id))
			.filter((c) => !term || (c.name ?? '').toLowerCase().includes(term))
			.slice(0, 25);
	}
	/** "+ Add campaigns" button → turns into the search input, focused and ready to type. */
	openCampaignAdd(): void {
		this.campaignAddOpen = true;
		this.campaignPickerOpen = true;
		setTimeout(() => this.campaignSearchInput?.nativeElement.focus());
	}
	onCampaignSearchBlur(): void {
		// Delay so a click on a dropdown item registers before the list disappears.
		setTimeout(() => {
			this.campaignPickerOpen = false;
			if (!this.campaignSearch.trim()) this.campaignAddOpen = false;
		}, 150);
	}
	addFormCampaign(id: number): void {
		if (this.formCampaignIds.includes(id)) return;
		this.formCampaignIds.push(id);
		this.campaignSearch = '';
		this.campaignSearchInput?.nativeElement.focus();
	}
	removeFormCampaign(id: number): void {
		this.formCampaignIds = this.formCampaignIds.filter((x) => x !== id);
	}
	moveFormCampaign(index: number, dir: -1 | 1): void {
		const to = index + dir;
		if (to < 0 || to >= this.formCampaignIds.length) return;
		const arr = this.formCampaignIds;
		[arr[index], arr[to]] = [arr[to], arr[index]];
	}
	/**
	 * "Start Calling" — creates a new instance of the agent (agents/start_instance) working the
	 * chosen campaign queue from the chosen phone number, and places its first call. The agent's
	 * card stays startable afterwards: another instance can be launched on other campaigns/number.
	 */
	saveCampaignQueue(): void {
		if (!this.configAgent || !this.selectedPhoneNumberId || !this.formCampaignIds.length || this.startingKey) return;
		this.launchInstance(this.configAgent, { campaign_ids: [...this.formCampaignIds] }, () => this.closeConfig());
	}

	/**
	 * Shared by every agent's Start: creates the instance (agents/start_instance) from the picked
	 * phone number and handles the outcome the same way — rejected (campaign/number taken), nothing
	 * to dial, paused on its first call, or started. `close` shuts the modal it was started from.
	 */
	private launchInstance(agent: AiAgent, fields: { campaign_ids: number[]; mode?: 'campaigns' | 'all_campaigns'; start_date?: string }, close: () => void): void {
		const descriptionId = AI_AGENT_CATALOG.find((x) => x.key === agent.key)?.id;
		if (descriptionId === undefined) {
			this.showToast(`${agent.name}: missing agent id — check AI_AGENT_CATALOG`);
			return;
		}
		this.startingKey = agent.key;
		this.aiAgentsSrv
			.startInstance({
				description_id: descriptionId,
				phone_number_id: this.selectedPhoneNumberId!,
				...fields,
				user_id: this.userId ?? undefined,
			})
			.subscribe({
				next: (res: any) => {
					const r = res?.body ?? res;
					this.startingKey = null;
					if (r?.status === 'rejected' && r.reason === 'campaign_in_use') {
						Swal.fire({
							icon: 'warning',
							title: 'Campaign already being called',
							text: `Another instance is already calling: ${r.message}. Remove that campaign or stop that instance first.`,
							confirmButtonText: 'Ok',
						});
						return;
					}
					if (r?.status === 'rejected' && r.reason === 'phone_in_use') {
						Swal.fire({
							icon: 'warning',
							title: 'Phone number already in use',
							text: `The phone number you're trying to use is already being used by another instance (${r.message}). Pick another one.`,
							confirmButtonText: 'Ok',
						});
						// Refreshes the picker so the taken number shows up in red (and drops the stale selection).
						this.phonePicker?.loadPhoneNumbers();
						return;
					}
					if (r?.status === 'finished') {
						Swal.fire({ icon: 'info', title: 'Nothing to dial', text: r.message, confirmButtonText: 'Ok' });
						return;
					}
					close();
					if (r?.status === 'paused') {
						// The instance exists (it keeps its queue and phone number) but its first call failed.
						this.refreshInstances();
						Swal.fire({
							icon: 'error',
							title: `${agent.name} #${r.agent_id} is paused`,
							text: `The first call failed: ${r.message}`,
							confirmButtonText: 'Ok',
						});
						return;
					}
					this.showToast(`${agent.name} #${r?.agent_id} started — calling from ${r?.phone_number}`);
					this.refreshInstances();
				},
				error: (err: any) => {
					this.startingKey = null;
					Swal.fire({
						icon: 'error',
						title: `Could not start ${agent.name}`,
						text: err?.error?.message ?? 'Check the Vapi integration and try again.',
						confirmButtonText: 'Ok',
					});
				},
			});
	}

	/**
	 * Parker's own Configure modal — same Start as Ivy (phone number → new instance that chains its
	 * calls), but instead of a campaign queue it picks ONE campaign or all of them, plus a start
	 * date: its queue (ai_parker_queue) is every lead whose meeting (leads.date_generated) falls
	 * between that date and today. The backend builds it in agents/start_instance.
	 */
	openParkerConfig(agent: AiAgent): void {
		this.formCampaignIds = [];
		this.campaignSearch = '';
		this.campaignPickerOpen = false;
		this.parkerMode = null;
		this.parkerStartDate = '';
		this.selectedPhoneNumberId = null;
		this.parkerConfigAgent = agent;
	}
	closeParkerConfig(): void {
		this.parkerConfigAgent = null;
	}
	setParkerMode(mode: 'campaigns' | 'all_campaigns'): void {
		this.parkerMode = mode;
	}
	/** Parker and Renee work a single campaign — picking one replaces the previous pick. */
	setSingleCampaign(id: number): void {
		this.formCampaignIds = [id];
		this.campaignSearch = '';
		this.campaignPickerOpen = false;
	}
	/** Today (local) as YYYY-MM-DD — the start date can't be later, the meetings must already have happened. */
	get today(): string {
		const d = new Date();
		return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
	}
	get canStartParker(): boolean {
		return (
			!!this.parkerMode &&
			(this.parkerMode === 'all_campaigns' || this.formCampaignIds.length === 1) &&
			!!this.parkerStartDate &&
			this.parkerStartDate <= this.today &&
			!!this.selectedPhoneNumberId &&
			!this.startingKey
		);
	}
	saveParkerConfig(): void {
		if (!this.parkerConfigAgent || !this.canStartParker) return;
		this.launchInstance(
			this.parkerConfigAgent,
			{
				mode: this.parkerMode!,
				campaign_ids: this.parkerMode === 'campaigns' ? [...this.formCampaignIds] : [],
				start_date: this.parkerStartDate,
			},
			() => this.closeParkerConfig(),
		);
	}

	/**
	 * Renee's own Configure modal — same Start as Ivy and Parker (phone number → new instance that
	 * chains its calls), picking ONE campaign or all of them, with no date: her queue
	 * (ai_queue_renee) is every lead pending a reschedule right now. Before each call the backend
	 * works out 3 free slots on the campaign's calendar for her to offer.
	 */
	openReneeConfig(agent: AiAgent): void {
		this.formCampaignIds = [];
		this.campaignSearch = '';
		this.campaignPickerOpen = false;
		this.reneeMode = null;
		this.selectedPhoneNumberId = null;
		this.reneeConfigAgent = agent;
	}
	closeReneeConfig(): void {
		this.reneeConfigAgent = null;
	}
	setReneeMode(mode: 'campaigns' | 'all_campaigns'): void {
		this.reneeMode = mode;
	}
	get canStartRenee(): boolean {
		return (
			!!this.reneeMode &&
			(this.reneeMode === 'all_campaigns' || this.formCampaignIds.length === 1) &&
			!!this.selectedPhoneNumberId &&
			!this.startingKey
		);
	}
	saveReneeConfig(): void {
		if (!this.reneeConfigAgent || !this.canStartRenee) return;
		this.launchInstance(
			this.reneeConfigAgent,
			{
				mode: this.reneeMode!,
				campaign_ids: this.reneeMode === 'campaigns' ? [...this.formCampaignIds] : [],
			},
			() => this.closeReneeConfig(),
		);
	}

	// ── Prospects modal — the queue of ONE instance (agents/get_prospects), opened from the Live floor ──
	openProspects(inst: any): void {
		const agent = this.instanceAgent(inst);
		if (!agent) return;
		this.prospectsAgent = agent;
		this.prospectsTitle = this.instanceLabel(inst);
		this.prospects = [];
		this.prospectsLoading = true;
		this.aiAgentsSrv.getProspects(Number(inst.id)).subscribe({
			next: (res: any) => {
				this.prospects = res?.body ?? res ?? [];
				this.prospectsLoading = false;
			},
			error: () => {
				this.prospectsLoading = false;
				this.showToast(`Could not load ${this.isLeadAgent(agent.key) ? 'leads' : 'prospects'} for ${this.prospectsTitle}`);
			},
		});
	}
	closeProspects(): void {
		this.prospectsAgent = null;
		this.prospects = [];
	}

	// ── Calls modal — from the Live floor (inst given) it's that instance's calls; from an agent's
	// card (no inst) it's the history of every instance ever created from that agent. ──
	openCalls(agent: AiAgent | undefined, inst?: any): void {
		if (!agent) return;
		const descriptionId = AI_AGENT_CATALOG.find((x) => x.key === agent.key)?.id;
		if (!inst && descriptionId === undefined) {
			this.showToast(`${agent.name}: missing agent id — check AI_AGENT_CATALOG`);
			return;
		}
		this.callsAgent = agent;
		this.callsTitle = inst ? this.instanceLabel(inst) : `${agent.name} · all instances`;
		this.calls = [];
		this.callsLoading = true;
		const request = inst ? this.aiAgentsSrv.getCalls(Number(inst.id)) : this.aiAgentsSrv.getCallsByDescription(descriptionId!);
		request.subscribe({
			next: (res: any) => {
				this.calls = res?.body ?? res ?? [];
				this.callsLoading = false;
			},
			error: () => {
				this.callsLoading = false;
				this.showToast(`Could not load calls for ${this.callsTitle}`);
			},
		});
	}
	closeCalls(): void {
		this.callsAgent = null;
		this.calls = [];
	}

	/**
	 * ai_calls.structure_data is Vapi's structured outputs, keyed by a random UUID per field (plus
	 * "summary" for Parker, added by the webhook) — each entry is { name, result }. Returns the
	 * result of the entry with that name, or null when it isn't there (call not analyzed yet, or
	 * another agent's call). PHP returns the JSON column as a raw string, so it's parsed here.
	 */
	structuredResult(call: any, name: string): any {
		let data: any = call.structure_data;
		if (typeof data === 'string') {
			try {
				data = JSON.parse(data);
			} catch {
				return null;
			}
		}
		if (!data || typeof data !== 'object') return null;
		const entry: any = Object.values(data).find((v: any) => v?.name === name);
		return entry?.result ?? null;
	}

	/**
	 * structuredResult() as display text: Vapi doesn't always return the type the schema asks for
	 * (a number, an object, a date sentence instead of an enum), so every value is coerced to a
	 * string here — never call string methods on the raw result. null when empty.
	 */
	structuredText(call: any, name: string): string | null {
		const v = this.structuredResult(call, name);
		if (v == null || v === '') return null;
		if (typeof v === 'string') return v.trim() || null;
		return typeof v === 'object' ? JSON.stringify(v) : String(v);
	}

	/**
	 * A date as Renee/Vapi may write it, to the datetime-local format "YYYY-MM-DDTHH:mm" (no
	 * timezone conversion — the time is kept as said). Understands "2026-10-20 13:00[:00]" and
	 * spoken dates like "Tuesday, October 20 at 1:00 PM AST" (no year = this year, or next year if
	 * that date already passed). '' when it can't be read.
	 */
	private parseLooseDate(raw: string | null): string {
		if (!raw) return '';
		const pad = (n: number) => String(n).padStart(2, '0');
		const iso = raw.match(/(\d{4}-\d{2}-\d{2})[ T](\d{1,2}):(\d{2})/);
		if (iso) return `${iso[1]}T${pad(Number(iso[2]))}:${iso[3]}`;

		const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
		const m = raw.match(/([A-Za-z]{3,})\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s*(\d{4})?[^0-9]*?(\d{1,2})(?::(\d{2}))?\s*([ap])\.?\s*m\b/i);
		if (!m) return '';
		const month = months.indexOf(m[1].slice(0, 3).toLowerCase());
		if (month < 0) return '';
		const day = Number(m[2]);
		let hour = Number(m[4]) % 12;
		if (m[6].toLowerCase() === 'p') hour += 12;
		const minute = Number(m[5] ?? 0);
		let year = m[3] ? Number(m[3]) : new Date().getFullYear();
		if (!m[3] && new Date(year, month, day, 23, 59) < new Date()) year++;
		return `${year}-${pad(month + 1)}-${pad(day)}T${pad(hour)}:${pad(minute)}`;
	}

	/**
	 * Ivy-only: buckets the "prospect_interest" structured output (result 1-10) into the three
	 * labels the Calls modal shows as a badge.
	 */
	prospectInterest(call: any): { label: string; level: 'bad' | 'medium' | 'interested' } | null {
		const result = this.structuredResult(call, 'prospect_interest');
		if (result == null) return null;
		if (result <= 3) return { label: 'BAD', level: 'bad' };
		if (result <= 5) return { label: 'MEDIUM', level: 'medium' };
		return { label: 'INTERESTED', level: 'interested' };
	}

	/**
	 * Parker-only: its structured outputs (reached_prospect, meeting_happened, no_show_party,
	 * sentiment, negative_reason, summary) as display-ready values for the Calls modal columns.
	 * level picks the badge color; null fields (call not analyzed yet) render as "—".
	 */
	parkerOutcome(call: any): {
		reached: { label: string; level: 'success' | 'danger' } | null;
		meeting: { label: string; level: 'success' | 'danger' | 'muted' } | null;
		noShow: string | null;
		sentiment: { label: string; level: 'success' | 'warning' | 'danger' | 'muted' } | null;
		negativeReason: string | null;
		summary: string | null;
	} {
		const reached = this.structuredResult(call, 'reached_prospect');
		const meeting = this.structuredText(call, 'meeting_happened')?.toLowerCase() ?? null;
		const noShow = this.structuredText(call, 'no_show_party')?.toLowerCase() ?? null;
		const sentiment = this.structuredText(call, 'sentiment')?.toLowerCase() ?? null;
		const sentimentLevel: Record<string, 'success' | 'warning' | 'danger'> = { positive: 'success', neutral: 'warning', negative: 'danger' };
		const noShowLabel: Record<string, string> = { closer: 'Closer', prospect: 'Prospect', both: 'Both' };
		return {
			reached: reached == null ? null : reached === true || reached === 'true' ? { label: 'Yes', level: 'success' } : { label: 'No', level: 'danger' },
			meeting: !meeting ? null : { label: meeting === 'yes' ? 'Yes' : meeting === 'no' ? 'No' : 'Unknown', level: meeting === 'yes' ? 'success' : meeting === 'no' ? 'danger' : 'muted' },
			noShow: noShow && noShow !== 'not_applicable' ? noShowLabel[noShow] ?? 'Unknown' : null,
			sentiment: !sentiment || sentiment === 'not_applicable' ? null : { label: sentiment.charAt(0).toUpperCase() + sentiment.slice(1), level: sentimentLevel[sentiment] ?? 'muted' },
			negativeReason: this.structuredText(call, 'negative_reason'),
			summary: this.structuredText(call, 'summary'),
		};
	}

	/**
	 * Renee-only: her structured outputs (reschedule_status, selected_option, new_meeting_datetime,
	 * sentiment) plus the call summary, as display-ready values for the Calls modal columns. level
	 * picks the badge color; null fields (call not analyzed yet) render as "—".
	 */
	reneeOutcome(call: any): {
		status: { label: string; level: 'success' | 'warning' | 'danger' | 'muted' } | null;
		option: string | null;
		newDate: string | null;
		sentiment: { label: string; level: 'success' | 'warning' | 'danger' | 'muted' } | null;
		summary: string | null;
	} {
		const status = this.structuredText(call, 'reschedule_status')?.toLowerCase() ?? null;
		const option = this.structuredText(call, 'selected_option');
		const sentiment = this.structuredText(call, 'sentiment')?.toLowerCase() ?? null;
		const statuses: Record<string, { label: string; level: 'success' | 'warning' | 'danger' | 'muted' }> = {
			rescheduled: { label: 'Rescheduled', level: 'success' },
			declined: { label: 'Declined', level: 'danger' },
			needs_callback: { label: 'Callback', level: 'warning' },
			not_reached: { label: 'Not reached', level: 'muted' },
		};
		const options: Record<string, string> = { option_1: 'Option 1', option_2: 'Option 2', option_3: 'Option 3', other: 'Other' };
		const sentimentLevel: Record<string, 'success' | 'warning' | 'danger'> = { positive: 'success', neutral: 'warning', negative: 'danger' };
		return {
			status: status ? statuses[status] ?? { label: status, level: 'muted' } : null,
			// selected_option should be option_1/2/3/other/none — anything else (Vapi sometimes writes the date itself) reads as "Other".
			option: option && option !== 'none' ? options[option.toLowerCase()] ?? 'Other' : null,
			newDate: this.structuredText(call, 'new_meeting_datetime') ?? (option && !options[option.toLowerCase()] && option !== 'none' ? option : null),
			sentiment: !sentiment || sentiment === 'not_applicable' ? null : { label: sentiment.charAt(0).toUpperCase() + sentiment.slice(1), level: sentimentLevel[sentiment] ?? 'muted' },
			summary: this.structuredText(call, 'summary'),
		};
	}

	// ── Renee: approve the reschedule she agreed on a call (lead/reschedule_qa, same as the lead page) ──
	/** Date picked per call (call id -> "YYYY-MM-DDTHH:mm"); unset until the user edits it, the default comes from the call. */
	rescheduleDates: Record<number, string> = {};
	/** Id of the call whose reschedule is being approved right now — disables its button meanwhile. */
	rescheduleBusyId: number | null = null;

	/**
	 * The date the picker shows for a Renee call: what the user picked, else the slot she agreed on
	 * — the proposed date of the chosen option (ai_queue_renee, exact), or Vapi's
	 * new_meeting_datetime for a counter-offer. '' when the call agreed on nothing.
	 */
	rescheduleDateFor(call: any): string {
		const picked = this.rescheduleDates[call.id];
		if (picked !== undefined) return picked;
		const option = this.structuredText(call, 'selected_option')?.toLowerCase() ?? '';
		const proposed = /^option_[123]$/.test(option) ? call[`proposed_date_${option.slice(-1)}`] : null;
		return (
			this.parseLooseDate(proposed ? String(proposed) : null) ||
			this.parseLooseDate(this.structuredText(call, 'new_meeting_datetime')) ||
			// Vapi sometimes puts the agreed date itself in selected_option.
			this.parseLooseDate(option ? this.structuredText(call, 'selected_option') : null)
		);
	}
	setRescheduleDate(call: any, value: string): void {
		this.rescheduleDates[call.id] = value;
	}
	/** The picker + Approve button show for calls Renee rescheduled, or that already have a date to start from. */
	canApproveReschedule(call: any): boolean {
		return !!call.lead_id && (this.structuredText(call, 'reschedule_status')?.toLowerCase() === 'rescheduled' || !!this.rescheduleDateFor(call));
	}
	isLeadRescheduled(call: any): boolean {
		return call.lead_replacement_status === 'Rescheduled';
	}

	/**
	 * "Approve reschedule": moves the lead's meeting to the picked date through lead/reschedule_qa —
	 * the same endpoint and payload as the lead page's reschedule, which updates leads.date_generated
	 * (replacement_status -> 'Rescheduled') and the lead's calendar event.
	 */
	approveReschedule(call: any): void {
		const value = this.rescheduleDateFor(call);
		const m = value.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/);
		if (!m) {
			this.showToast('Pick a valid date and time first');
			return;
		}
		if (!call.calendar_id) {
			this.showToast(`Lead #${call.lead_id} has no calendar event to move`);
			return;
		}
		const [, date, hh, mm] = m;
		const newTime = `${hh}:${mm}:00`;
		const hour24 = Number(hh);
		const label = new Date(`${date}T${newTime}`).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
		Swal.fire({
			icon: 'question',
			title: `Reschedule lead #${call.lead_id}?`,
			text: `${call.prospect_name ?? 'The prospect'}'s meeting will be moved to ${label}.`,
			showCancelButton: true,
			confirmButtonText: 'Approve reschedule',
			cancelButtonText: 'Cancel',
		}).then((r) => {
			if (!r.isConfirmed) return;
			this.rescheduleBusyId = Number(call.id);
			this.leadSrv
				.rescheduleQa({
					id: null,
					lead_id: call.lead_id,
					alias: this.userLogin,
					new_date: `${date} ${newTime}`,
					newHour: String(hour24 % 12 || 12).padStart(2, '0'),
					newMin: mm,
					ampm: hour24 >= 12 ? 'PM' : 'AM',
					reason: '',
					status: 'Pending',
					resolved_by: this.userId,
					newTime,
					calendar_id: call.calendar_id,
					user_login: this.userLogin,
					user_id: this.userId,
					send_notification: false,
				})
				.subscribe({
					next: (res: any) => {
						this.rescheduleBusyId = null;
						const body = res?.body ?? res;
						if (body?.error) {
							Swal.fire({ icon: 'error', title: 'Could not reschedule', text: body.error, confirmButtonText: 'Ok' });
							return;
						}
						call.lead_replacement_status = 'Rescheduled';
						this.showToast(`Lead #${call.lead_id} rescheduled to ${label}`);
					},
					error: (err: any) => {
						this.rescheduleBusyId = null;
						Swal.fire({ icon: 'error', title: 'Could not reschedule', text: err?.error?.message ?? 'Try again.', confirmButtonText: 'Ok' });
					},
				});
		});
	}

	// ── Call detail — recording (fresh signed URL, never cached — Vapi mints a new one each
	// request) + transcript (ai_calls.transcripts is a JSON column; PHP returns it as a raw
	// string, so it needs parsing), shown together in one modal. ──
	openCallDetail(call: any): void {
		this.callDetail = call;
		this.recordingUrl = null;
		this.recordingLoading = !!call.vapi_call_id;

		let parsed: any = call.transcripts;
		if (typeof parsed === 'string') {
			try {
				parsed = JSON.parse(parsed);
			} catch {
				parsed = [];
			}
		}
		this.transcriptMessages = Array.isArray(parsed) ? parsed : [];

		if (!call.vapi_call_id) return;
		this.vapiSrv.getRecordingUrl(call.vapi_call_id).subscribe({
			next: (res: any) => {
				this.recordingUrl = res?.body?.url ?? res?.url ?? null;
				this.recordingLoading = false;
			},
			error: () => {
				this.recordingLoading = false;
				this.showToast('Could not fetch the recording URL');
			},
		});
	}
	closeCallDetail(): void {
		this.callDetail = null;
		this.recordingUrl = null;
		this.transcriptMessages = [];
	}

	/** Key of the agent whose instance is being created right now — disables its Start buttons meanwhile. */
	startingKey: string | null = null;

	// ── Live floor — running agent instances (agents/get_instances) ──
	refreshInstances(): void {
		this.aiAgentsSrv.getInstances().subscribe({
			next: (res: any) => {
				this.instances = res?.body ?? res ?? [];
				this.instancesLoaded = true;
				// Only look a phone id up once: an id Vapi doesn't know stays unresolved, and must not
				// trigger a new Vapi request on every poll.
				const unknown = this.instances.map((i) => i.phone_number_id).filter((id) => id && !this.phoneIdsLookedUp.has(id));
				if (unknown.length) {
					unknown.forEach((id) => this.phoneIdsLookedUp.add(id));
					this.loadPhoneNumbers();
				}
			},
			error: () => {
				this.instances = [];
				this.instancesLoaded = true;
			},
		});
	}
	/** Phone ids already resolved (or tried) against Vapi — see refreshInstances(). */
	private phoneIdsLookedUp = new Set<string>();
	private loadPhoneNumbers(): void {
		this.phoneNumbersSrv.getAvailablePhoneNumbers().subscribe({
			next: (res: any) => this.onPhoneNumbersLoaded(res?.body ?? res ?? []),
			error: () => {},
		});
	}
	/** Also fed by the Configure modal's phone picker, so starting an instance needs no extra lookup for the Live floor. */
	onPhoneNumbersLoaded(phones: { phone_number_id: string; phone_number: string }[]): void {
		for (const p of phones) {
			this.phoneNumberById[p.phone_number_id] = p.phone_number;
			this.phoneIdsLookedUp.add(p.phone_number_id);
		}
	}

	/** The catalog agent (roster entry: icon, key, ...) an instance was created from. */
	instanceAgent(inst: any): AiAgent | undefined {
		return this.agentByKey(AI_AGENT_CATALOG.find((c) => c.id === Number(inst.description_id))?.key ?? null);
	}
	/** Parker and Renee work leads (their own queue tables), the other agents work prospects. */
	isLeadAgent(key: string | null | undefined): boolean {
		return key === 'parker' || key === 'renee';
	}
		/** "Ivy #1001" — instances of the same agent are told apart by their id. */
	instanceLabel(inst: any): string {
		return `${inst.name} #${inst.id}`;
	}
	/** "(209) 254-4263" — Vapi gives E.164 ("+12092544263"); the US country code is dropped. Anything else is shown as it comes. */
	instancePhone(inst: any): string {
		const phone = this.phoneNumberById[inst.phone_number_id];
		if (!phone) return '—';
		const digits = phone.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
		return digits.length === 10 ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}` : phone;
	}
	/** Running (active or paused) instances of one catalog agent — shown on its card in the Agents tab. */
	instancesOf(agent: AiAgent): any[] {
		const descriptionId = AI_AGENT_CATALOG.find((x) => x.key === agent.key)?.id;
		return this.runningInstances.filter((i) => Number(i.description_id) === descriptionId);
	}
	/** Instances still working (active or paused) — a finished one no longer dials nor holds its phone number. */
	get runningInstances(): any[] {
		return this.instances.filter((i) => i.status !== 'finished');
	}
	get finishedCount(): number {
		return this.instances.length - this.runningInstances.length;
	}
	/** Rows of the Live floor table: every instance, or only the running ones when "Hide finished" is on. */
	get floorInstances(): any[] {
		return this.hideFinished ? this.runningInstances : this.instances;
	}
	setHideFinished(hide: boolean): void {
		this.hideFinished = hide;
		try {
			localStorage.setItem(HIDE_FINISHED_KEY, hide ? '1' : '0');
		} catch {}
	}
	/**
	 * A finished instance either ran out of prospects or was stopped by the user; the table doesn't
	 * store which, so it's told apart by whether its queue still had pending prospects.
	 */
	wasStopped(inst: any): boolean {
		return inst.status === 'finished' && Number(inst.queue_called) < Number(inst.queue_total);
	}
	/** True while the instance's latest action is a call that hasn't been reported as ended yet. */
	isInstanceCalling(inst: any): boolean {
		return inst.last_action === 'CALLING';
	}

	// ── Campaigns modal — the full, ordered campaign list of ONE instance (the Live floor row only shows the first) ──
	campaignsModal: { title: string; icon: string; campaigns: string[] } | null = null;
	openCampaigns(inst: any): void {
		this.campaignsModal = { title: this.instanceLabel(inst), icon: this.instanceAgent(inst)?.icon ?? '', campaigns: inst.campaigns ?? [] };
	}
	closeCampaigns(): void {
		this.campaignsModal = null;
	}

	pauseInstance(inst: any): void {
		this.runInstanceAction(inst, 'pause');
	}
	resumeInstance(inst: any): void {
		this.runInstanceAction(inst, 'resume');
	}
	stopInstance(inst: any): void {
		Swal.fire({
			icon: 'warning',
			title: `Stop ${this.instanceLabel(inst)}?`,
			text: "It won't place any more calls and its phone number is freed. This can't be undone — a call in progress is not cut.",
			showCancelButton: true,
			confirmButtonText: 'Stop',
			cancelButtonText: 'Cancel',
		}).then((r) => {
			if (r.isConfirmed) this.runInstanceAction(inst, 'stop');
		});
	}
	private runInstanceAction(inst: any, action: 'pause' | 'resume' | 'stop'): void {
		if (this.instanceBusyId !== null) return;
		const label = this.instanceLabel(inst);
		this.instanceBusyId = Number(inst.id);
		this.aiAgentsSrv.setInstanceAction(Number(inst.id), action).subscribe({
			next: (res: any) => {
				const r = res?.body ?? res;
				this.instanceBusyId = null;
				if (action === 'resume' && r?.status === 'paused') {
					Swal.fire({ icon: 'error', title: `${label} is still paused`, text: `The call failed: ${r.message}`, confirmButtonText: 'Ok' });
				} else if (action === 'resume' && r?.status === 'finished') {
					Swal.fire({ icon: 'info', title: `${label} finished`, text: r.message, confirmButtonText: 'Ok' });
				} else {
					this.showToast(`${label}: ${action === 'pause' ? 'paused' : action === 'resume' ? 'resumed' : 'stopped'}`);
				}
				this.refreshInstances();
			},
			error: (err: any) => {
				this.instanceBusyId = null;
				this.showToast(err?.error?.message ?? `Could not ${action} ${label}`);
				this.refreshInstances();
			},
		});
	}

	// ── Stats (real, straight from Vapi's own Analytics API — one call, every agent) ──
	refreshStats(): void {
		this.vapiSrv.getVapiStats(this.period).subscribe({
			next: (res: any) => {
				const rows = res?.body ?? res ?? [];
				const byAgent: Record<string, any> = {};
				for (const row of rows) byAgent[row.agent_key] = row;
				this.statsByAgent = byAgent;
			},
			error: () => (this.statsByAgent = {}),
		});
	}
	setPeriod(p: StatsPeriod): void {
		this.period = p;
		this.refreshStats();
	}

	private showToast(msg: string): void {
		this.toast = msg;
		if (this.toastTimer) clearTimeout(this.toastTimer);
		this.toastTimer = setTimeout(() => (this.toast = ''), 3200);
	}
}
